import { useMemo, useRef, useState } from "react";
import Icon from "domains/ui/components/Icon";
import ButtonSingleChoice, {
  ButtonSingleChoiceProps,
} from "domains/ui/components/Menu/ButtonSingleChoice";
import MenuItem from "domains/ui/components/Menu/MenuItem";
import MenuTitle from "domains/ui/components/Menu/MenuTitle";
import ScenarioInput from "domains/ui/components/ScenarioInput";

import { Box, Menu, MenuDivider, MenuList } from "@chakra-ui/react";

type OptionsProps<T> =
  | {
      value: T;
      label: string;
      dataTestId?: string;
      isDivider?: never;
      isDisabled?: boolean;
    }
  | {
      isDivider: true;
      value?: never;
      label?: never;
      dataTestId?: never;
      isDisabled?: never;
    };

export type MenuListSingleChoiceProps<T> = Omit<
  ButtonSingleChoiceProps,
  "text" | "hasValue"
> & {
  placeholder: string;
  options: OptionsProps<T>[];
  dataTestId?: string;
  topText?: string;
  variant?: ButtonSingleChoiceProps["variant"];
  size?: string;
  isButtonLoading?: boolean;
  undefinedValue?: T;
  isWithSearch?: boolean;
  maxItems?: number;
} & (
    | {
        cannotBeUndefined: true;
        selectedValue: T;
        setSelectedValue: (value: T) => void;
      }
    | {
        cannotBeUndefined?: false;
        selectedValue: T | undefined;
        setSelectedValue: (value: T | undefined) => void;
      }
  );

export default function MenuListSingleChoice<T extends string>({
  placeholder,
  options,
  setSelectedValue,
  selectedValue,
  undefinedValue,
  dataTestId,
  topText,
  variant,
  size,
  cannotBeUndefined,
  isButtonLoading = false,
  isWithSearch = false,
  maxItems = 100,
  ...props
}: MenuListSingleChoiceProps<T>) {
  const [search, setSearch] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const mapValueLabel = useMemo(() => {
    return options.reduce((acc, option) => {
      if (!option.isDivider) {
        acc[option.value] =
          option.label.match(/(.*) \(\d*\)$/)?.[1] ?? option.label;
      }
      return acc;
    }, {} as Record<string, string>);
  }, [options]);

  const displayedOptions = useMemo(() => {
    const filteredOptions = !search
      ? options
      : options.filter((option) => {
          if (option.isDivider) {
            return false;
          }
          return option.label.toLowerCase().includes(search.toLowerCase());
        });

    if (maxItems === -1) {
      return filteredOptions;
    }

    return filteredOptions.filter((option, index) => {
      if (selectedValue && selectedValue === option.value) {
        return true;
      }
      return index < maxItems;
    });
  }, [options, maxItems, selectedValue, search]);

  return (
    <Menu isLazy variant="blurred">
      <ButtonSingleChoice
        asMenuButton
        text={
          <Box overflow="hidden" isTruncated>
            {selectedValue && selectedValue !== undefinedValue
              ? mapValueLabel[selectedValue]
              : placeholder}
          </Box>
        }
        onRemove={
          (undefinedValue && (() => setSelectedValue(undefinedValue))) ||
          (!cannotBeUndefined && (() => setSelectedValue(undefined))) ||
          undefined
        }
        hasValue={!!selectedValue && selectedValue !== undefinedValue}
        dataTestId={dataTestId}
        variant={variant}
        size={size}
        isLoading={isButtonLoading}
        maxW="300px"
        {...props}
      />

      <MenuList
        overflow="hidden"
        overflowY="auto"
        maxW="400px"
        maxH="400px"
        onFocus={() => {
          if (isWithSearch) {
            inputRef.current?.focus({
              preventScroll: true,
            });
          }
        }}
        rootProps={{ maxWidth: "100%", minWidth: "unset" }}
      >
        {topText && (
          <MenuTitle variant="blurred" text={topText.toUpperCase()} mx={2} />
        )}

        {isWithSearch && (
          <ScenarioInput
            ref={inputRef}
            value={search}
            setValue={setSearch}
            placeholder={"Search"}
            leftComponent={
              <Icon mb={0.5} id={"Ui/Search"} color={"textTertiary"} />
            }
            containerProps={{
              mb: displayedOptions.length > 0 ? 1.5 : 0,
              flexShrink: 0,
            }}
          />
        )}

        {displayedOptions.map((option, idx) => {
          if (option.isDivider) {
            return <MenuDivider key={idx} mx={1} />;
          } else {
            return (
              <MenuItem
                key={option.value}
                check="icon"
                text={option.label}
                isSelected={selectedValue === option.value}
                onClick={() => setSelectedValue(option.value)}
                dataTestId={option.dataTestId}
                isDisabled={option.isDisabled}
              />
            );
          }
        })}
      </MenuList>
    </Menu>
  );
}

export function MenuListSingleChoiceNeverUndefined<T extends string>({
  placeholder,
  options,
  setSelectedValue,
  selectedValue,
  dataTestId,
  topText,
  variant,
  isButtonLoading,
  isWithSearch,
  maxItems,
}: Omit<
  MenuListSingleChoiceProps<T>,
  "cannotBeUndefined" | "setSelectedValue" | "selectedValue"
> & {
  setSelectedValue: (value: T) => void;
  selectedValue: T;
}) {
  return (
    <MenuListSingleChoice
      placeholder={placeholder}
      options={options}
      setSelectedValue={(value) => {
        if (value === undefined) {
          return;
        }
        setSelectedValue(value);
      }}
      selectedValue={selectedValue}
      dataTestId={dataTestId}
      topText={topText}
      variant={variant}
      isButtonLoading={isButtonLoading}
      isWithSearch={isWithSearch}
      maxItems={maxItems}
      cannotBeUndefined
    />
  );
}
